/**
 * Capacitor Integration
 * 
 * Handles platform-specific implementations and optimizations.
 * Provides iOS/Android native API integration, web platform fallbacks,
 * platform-specific optimizations, and native event bridge management.
 */

import { Network, ConnectionStatus, ConnectionType } from '@capacitor/network';
import { Capacitor } from '@capacitor/core';

/**
 * Platform capabilities
 */
interface PlatformCapabilities {
  hasNativeNetworking: boolean;
  supportsQualityDetection: boolean;
  supportsConnectionTypeDetection: boolean;
  supportsBackgroundMonitoring: boolean;
  platform: 'ios' | 'android' | 'web';
}

/**
 * Capacitor Integration
 * 
 * Manages platform-specific network functionality and provides
 * graceful fallbacks for web platforms.
 */
export class CapacitorIntegration {
  private capabilities: PlatformCapabilities;
  private enableDebugLogging: boolean;

  constructor(enableDebugLogging = true) {
    this.enableDebugLogging = enableDebugLogging;
    this.capabilities = this.detectPlatformCapabilities();
    
    this.debugLog('🔧 Capacitor integration initialized:', this.capabilities);
  }

  /**
   * Detect platform capabilities
   */
  private detectPlatformCapabilities(): PlatformCapabilities {
    const platform = Capacitor.getPlatform() as 'ios' | 'android' | 'web';
    const isNative = Capacitor.isNativePlatform();

    return {
      hasNativeNetworking: isNative,
      supportsQualityDetection: isNative,
      supportsConnectionTypeDetection: isNative,
      supportsBackgroundMonitoring: isNative,
      platform
    };
  }

  /**
   * Get platform capabilities
   */
  getCapabilities(): PlatformCapabilities {
    return { ...this.capabilities };
  }

  /**
   * Check if running on native platform
   */
  isNativePlatform(): boolean {
    return this.capabilities.hasNativeNetworking;
  }

  /**
   * Get network status with platform-specific optimizations
   */
  async getNetworkStatus(): Promise<ConnectionStatus> {
    try {
      if (this.capabilities.hasNativeNetworking) {
        // Use native Capacitor Network plugin
        const status = await Network.getStatus();
        this.debugLog('🔧 Native network status:', status);
        return status;
      } else {
        // Web fallback using navigator.onLine
        const status: ConnectionStatus = {
          connected: navigator.onLine,
          connectionType: this.getWebConnectionType()
        };
        this.debugLog('🔧 Web fallback network status:', status);
        return status;
      }
    } catch (error) {
      console.error('❌ Failed to get network status:', error);
      
      // Ultimate fallback
      return {
        connected: navigator.onLine,
        connectionType: 'unknown'
      };
    }
  }

  /**
   * Add network status listener with platform-specific handling
   */
  addNetworkStatusListener(callback: (status: ConnectionStatus) => void): void {
    if (this.capabilities.hasNativeNetworking) {
      // Use native Capacitor listener
      Network.addListener('networkStatusChange', callback);
      this.debugLog('🔧 Native network listener added');
    } else {
      // Web fallback using online/offline events
      const handleOnline = () => {
        callback({
          connected: true,
          connectionType: this.getWebConnectionType()
        });
      };

      const handleOffline = () => {
        callback({
          connected: false,
          connectionType: 'none'
        });
      };

      window.addEventListener('online', handleOnline);
      window.addEventListener('offline', handleOffline);
      
      this.debugLog('🔧 Web fallback listeners added');
    }
  }

  /**
   * Remove all network listeners
   */
  removeAllNetworkListeners(): void {
    if (this.capabilities.hasNativeNetworking) {
      Network.removeAllListeners();
      this.debugLog('🔧 Native network listeners removed');
    } else {
      // Remove web event listeners
      // Note: In a real implementation, we'd need to track the specific listeners
      // For now, we'll just log that cleanup is needed
      this.debugLog('🔧 Web listeners cleanup needed (implementation specific)');
    }
  }

  /**
   * Get connection type for web platform
   */
  private getWebConnectionType(): ConnectionType {
    // Try to detect connection type on web
    if ('connection' in navigator) {
      const connection = (navigator as unknown as { connection?: { effectiveType?: string } }).connection;
      
      if (connection) {
        // Map effective type to our connection types
        switch (connection.effectiveType) {
          case '4g':
            return 'cellular';
          case '3g':
          case '2g':
            return 'cellular';
          case 'slow-2g':
            return 'cellular';
          default:
            return 'wifi'; // Assume wifi for unknown fast connections
        }
      }
    }

    // Default to wifi for web
    return 'wifi';
  }

  /**
   * Perform platform-optimized quality check
   */
  async performQualityCheck(url: string, timeout: number): Promise<{
    success: boolean;
    latency: number;
    error?: string;
  }> {
    const startTime = Date.now();

    try {
      if (this.capabilities.hasNativeNetworking) {
        // Native platform - can use full fetch with timeout
        const response = await fetch(url, {
          method: 'HEAD',
          signal: AbortSignal.timeout(timeout)
        });

        const latency = Date.now() - startTime;
        
        return {
          success: response.ok,
          latency
        };
      } else {
        // Web platform - simplified check
        const response = await Promise.race([
          fetch(url, { method: 'HEAD' }),
          new Promise<never>((_, reject) => 
            setTimeout(() => reject(new Error('Timeout')), timeout)
          )
        ]);

        const latency = Date.now() - startTime;
        
        return {
          success: response.ok,
          latency
        };
      }
    } catch (error) {
      const latency = Date.now() - startTime;
      
      return {
        success: false,
        latency: Math.min(latency, timeout),
        error: error instanceof Error ? error.message : 'Unknown error'
      };
    }
  }

  /**
   * Get platform-specific configuration recommendations
   */
  getRecommendedConfig(): {
    qualityCheckInterval: number;
    qualityCheckTimeout: number;
    enableQualityMonitoring: boolean;
  } {
    if (this.capabilities.hasNativeNetworking) {
      // Native platforms can handle more frequent checks
      return {
        qualityCheckInterval: 30000, // 30 seconds
        qualityCheckTimeout: 5000,   // 5 seconds
        enableQualityMonitoring: true
      };
    } else {
      // Web platforms should be more conservative
      return {
        qualityCheckInterval: 60000, // 60 seconds
        qualityCheckTimeout: 3000,   // 3 seconds
        enableQualityMonitoring: true
      };
    }
  }

  /**
   * Check if background monitoring is supported
   */
  supportsBackgroundMonitoring(): boolean {
    return this.capabilities.supportsBackgroundMonitoring;
  }

  /**
   * Get platform information
   */
  getPlatformInfo(): {
    platform: string;
    isNative: boolean;
    capabilities: PlatformCapabilities;
  } {
    return {
      platform: this.capabilities.platform,
      isNative: this.capabilities.hasNativeNetworking,
      capabilities: this.capabilities
    };
  }

  /**
   * Debug logging helper
   */
  private debugLog(message: string, data?: unknown): void {
    if (this.enableDebugLogging) {
      if (data) {
        console.log(message, data);
      } else {
        console.log(message);
      }
    }
  }

  /**
   * Cleanup platform-specific resources
   */
  cleanup(): void {
    this.removeAllNetworkListeners();
    this.debugLog('🧹 Capacitor integration cleaned up');
  }
}
